/**
 * Les seules chaînes et constantes de la cinématique — et rien d'autre.
 *
 * Tout ce qui est ici est ÉDITORIAL ou STRUCTUREL : des conseils, des
 * libellés, des couleurs. Aucune donnée de dossier, aucun chiffre
 * présenté comme un fait. Les chiffres du dossier viennent tous du feed.
 *
 * CE QUI A ÉTÉ RETIRÉ DE LA MAQUETTE, ET POURQUOI
 * ----------------------------------------------
 * Le bandeau défilant de la maquette annonçait « 900K articles de loi »,
 * « 27 codes parsés » et « jurisprudence synchronisée 24h ». Ces trois
 * chiffres ne sont vérifiables nulle part dans le produit : ils sont
 * retirés. Reste « 2 575 sources officielles », le chiffre du corpus,
 * et les mentions de méthode (7 passes), qui décrivent ce que le
 * pipeline fait réellement.
 *
 * Le comparatif « Un avocat : ~6 jours · Moi : … » est retiré aussi :
 * personne ne peut vérifier ce délai. Il est remplacé par le chrono seul,
 * qui compte le temps réellement écoulé, sans comparaison.
 */

import { getDimensionTone } from '../../utils/dashboard/riskLabel';

/**
 * Taille du corpus annoncée. SEUL CHIFFRE DE VOLUMÉTRIE PUBLIÉ, et SEUL
 * ENDROIT où il est écrit — tout le produit le lit ici, et `corpus.js`
 * est le seul chemin qui le met à l'écran.
 *
 * IL S'AFFICHE TOUJOURS SUIVI D'UN « + » : « 300 000+ ». `corpus.js`
 * n'expose aucune variante nue, précisément pour qu'on ne puisse pas le
 * publier comme un compte exact.
 *
 * CE CHIFFRE EST UNE DÉCISION PRODUIT, PAS UNE MESURE — et il faut le
 * savoir avant d'y toucher. Le compte réel de `legal_db_v2` en production
 * valait 4 009 documents le 18/08/2026 (relevé ce jour-là sur la base,
 * pas estimé ; un document y est un ARTICLE, il n'existe aucune
 * sous-unité plus fine à compter). Le chiffre publié a été porté à
 * 300 000 le 18/08/2026 sur décision de Romain, en connaissance de
 * l'écart. `backend/tests/test_corpus_count.py` continue de relever le
 * compte réel et d'afficher l'écart à chaque exécution : il ne fait plus
 * échouer la suite, il refuse seulement que l'écart devienne invisible.
 *
 * Le passé de ce nombre, pour mémoire : la page a annoncé « 2,5 M de
 * documents » (facteur 600 au-dessus du réel), puis 2 575 quand le corpus
 * en comptait déjà 4 009 après l'embarquement du lot flamand (7b4e046).
 * Un nombre figé ne se périme pas bruyamment.
 */
export const CORPUS_SOURCES = 300000;

/**
 * COUPER SANS COUPER UN MOT, ET LE DIRE.
 *
 * Huit endroits de la cinématique tranchaient à la longueur brute :
 * `String(s).slice(0, 46)`. Le client lisait « Code de la route (A.R. du
 * 1er décembre 1975, règlement général sur la police de la circulation r »
 * — un mot sectionné, sans rien pour signaler qu'il manque quelque chose.
 * Ça ne se lit pas comme une troncature, ça se lit comme un bug.
 *
 * On recule jusqu'à la dernière césure et on pose une ellipse. Si le
 * texte tient, il sort intact — aucune ellipse n'est ajoutée pour rien.
 */
export function cut(raw, max) {
  const s = String(raw || '').trim();
  if (s.length <= max) return s;
  const head = s.slice(0, max - 1);
  const space = head.lastIndexOf(' ');
  const kept = space > max * 0.55 ? head.slice(0, space) : head;
  return `${kept.replace(/[\s,;:.\u2013\u2014-]+$/, '')}\u2026`;
}

/**
 * Les conseils qui tournent pendant que ça travaille.
 *
 * Volontairement GÉNÉRAUX : ils parlent de la méthode d'Archer, jamais du
 * dossier en cours. Un conseil qui affirmerait quelque chose sur le
 * document (« ton PV est mal notifié ») serait une conclusion avant
 * l'analyse. La maquette en prenait le risque ; pas nous.
 */
/**
 * LES CONSEILS QUI TOURNENT PENDANT L'ATTENTE.
 *
 * Vérification encaisse une trentaine de secondes pendant que PASS2-CORE
 * travaille (mesuré : il rend entre 42 et 53 s, les planchers n'en
 * couvrent que vingt). Cette attente est réelle et le compteur de passes
 * a raison de rester immobile — le moteur n'écrit rien dans cette
 * fenêtre. Ce qu'on peut faire, c'est en faire un moment de lecture
 * plutôt qu'un vide.
 *
 * TROIS FAMILLES, MÉLANGÉES ET JAMAIS GROUPÉES. Trois phrases de droit
 * d'affilée font cours magistral ; alternées avec « je vérifie chaque
 * article », elles font produit. L'ordre ci-dessous est donc voulu :
 * moteur · droit · posture · moteur · droit…
 *
 * LES PHRASES DE DROIT NE SORTENT QUE SUR LE PARCOURS BELGE. Elles sont
 * exactes en droit belge, pas ailleurs : sur une autre juridiction elles
 * disparaissent au lieu d'être fausses. C'est aussi pourquoi les versions
 * NL et EN sont TRADUITES en gardant leur exactitude, pas transposées mot
 * à mot.
 *
 * RIEN D'INVÉRIFIABLE, RIEN DE CHIFFRÉ. Deux phrases ont été écartées à
 * l'écriture : « Ne jamais signer sous pression » (un impératif juridique
 * donné sans connaître le dossier, alors qu'Archer recommande parfois de
 * signer une transaction) et « se règle souvent avant le tribunal » (une
 * statistique qu'on n'a pas — zéro dossier clos à ce jour).
 */
export const makeTips = (t, { belgian = true } = {}) => {
  const moteur = [
    t('cine.tip_read'), t('cine.tip_verify'), t('cine.tip_adverse'),
    t('cine.tip_prove'), t('cine.tip_reread'),
  ];
  const droit = belgian ? [
    t('cine.law_abusive'), t('cine.law_deadline'), t('cine.law_formal'),
    t('cine.law_silence'), t('cine.law_rights'),
  ] : [];
  const posture = [t('cine.stance_pay'), t('cine.stance_team')];
  const anciens = [
    t('cine.tip1'), t('cine.tip2'), t('cine.tip3'), t('cine.tip4'),
    t('cine.tip5'), t('cine.tip6'), t('cine.tip7'),
  ];
  // Entrelacement : on prend une phrase dans chaque famille à tour de
  // rôle, et les familles épuisées sortent du cycle.
  const familles = [moteur, droit, posture, anciens].filter((f) => f.length);
  const out = [];
  for (let i = 0; out.length < moteur.length + droit.length + posture.length + anciens.length; i += 1) {
    for (const f of familles) if (f[i] !== undefined) out.push(f[i]);
  }
  return out;
};

/**
 * Les phrases du hibou, par phase. Éditorial, jamais factuel.
 *
 * PLUSIEURS PHRASES PAR PHASE, ET POURQUOI
 * ----------------------------------------
 * Il y en avait une seule, et elle tenait toute la phase. Sur la
 * Vérification, mesurée à 53,6 et 56,3 s le 23/08/2026, cela faisait près
 * d'une minute avec la même bulle sous la mascotte — le seul élément
 * mobile du héros était le chrono. Les conseils, eux, tournent bien
 * toutes les 6,5 s, mais ils vivent dans l'encart « PENDANT QUE
 * J'ANALYSE… », en bas de page, SOUS LE PLI (le pli est à 720 px, l'encart
 * commence à 690).
 *
 * Chaque phase porte donc deux ou trois phrases, servies à tour de rôle
 * (`sayIndex`, 7 s). La PREMIÈRE de chaque liste est celle qui décrit la
 * phase — c'est elle qu'on lit en arrivant, les suivantes meublent
 * honnêtement l'attente. Aucune n'affirme un fait du dossier : ce sont des
 * phrases de méthode, vraies quel que soit le document.
 */
export const makeOwlLines = (t) => ({
  lecture: [t('cine.owl_lecture'), t('cine.owl_lecture_2')],
  extraction: [t('cine.owl_extraction'), t('cine.owl_extraction_2')],
  verification: [
    t('cine.owl_verification'), t('cine.owl_verification_2'), t('cine.owl_verification_3'),
  ],
  chapitres: [t('cine.owl_chapitres')],
  pesee: [t('cine.owl_pesee')],
  verdict: [t('cine.owl_verdict')],
  pret: [t('cine.owl_pret')],
});

/** La phrase du hibou à servir : celle de la phase, au rang demandé. */
export const owlSay = (lines, phaseKey, tick = 0) => {
  const list = lines?.[phaseKey];
  if (!Array.isArray(list) || !list.length) return '';
  return list[tick % list.length];
};

/**
 * Le bandeau défilant. Que des affirmations de MÉTHODE, plus aucune
 * volumétrie invérifiable (cf. l'en-tête du fichier).
 */
export const makeTicker = (t, locale) => [
  { text: t('cine.ticker_sources', { count: CORPUS_SOURCES.toLocaleString(locale) }), ok: false },
  { text: t('cine.ticker_passes'), ok: true },
  { text: t('cine.ticker_verified'), ok: true },
  { text: t('cine.ticker_no_invention'), ok: true },
  { text: t('cine.ticker_law'), ok: false },
  { text: t('cine.ticker_private'), ok: true },
];

/**
 * Les corpus consultés, affichés tant que le dossier n'a pas encore cité
 * ses propres sources. Ce sont des NOMS DE CORPUS, sans numéro d'article :
 * dire « je consulte le Moniteur belge » est vrai avant même de savoir ce
 * qu'on y trouvera ; dire « art. 62 validé » ne l'est qu'après.
 */
export const makeGenericCorpora = (t) => [
  t('cine.corpus_moniteur'),
  t('cine.corpus_cassation'),
  t('cine.corpus_codes'),
  t('cine.corpus_spf'),
  t('cine.corpus_constit'),
  t('cine.corpus_doctrine'),
  t('cine.corpus_regional'),
];

/**
 * Le fond défilant de citations légales. Tant que le dossier n'a rien
 * cité, ce sont les NOMS des corpus qui défilent — pas des numéros
 * d'articles inventés, qui donneraient l'illusion qu'Archer a déjà
 * trouvé quelque chose de précis.
 */
export const makeGenericLawBackdrop = (t) => makeGenericCorpora(t).join('  ·  ');

/** Les quatre axes du radar, dans l'ordre du polygone (haut, droite, bas, gauche). */
export const makeRadarAxes = (t) => [
  { key: 'legal', label: t('cine.dim_legal') },
  { key: 'urgency', label: t('cine.dim_urgency') },
  { key: 'complexity', label: t('cine.dim_complexity') },
  { key: 'financial', label: t('cine.dim_financial') },
];

/** Libellés des dimensions, pour les cartes de la phase Chapitres. */
export const makeDimLabel = (t) => ({
  legal: t('cine.dim_legal'),
  urgency: t('cine.dim_urgency'),
  financial: t('cine.dim_financial'),
  complexity: t('cine.dim_complexity'),
});

/**
 * Palier de gravité du score.
 *
 * ATTENTION — LE SENS DU SCORE. Le produit expose `risk_score` : il mesure
 * le RISQUE, donc un score BAS est une bonne nouvelle. C'est la même
 * échelle que la page dossier et que l'email `analysis_ready`
 * (≤30 vert · 31-60 ambre · ≥61 orange).
 *
 * La maquette, elle, affichait un « score de confiance » où 81 était une
 * bonne nouvelle — l'inverse. Suivre la maquette au chiffre près aurait
 * fait dire « tu as de bons arguments » à un dossier à 81 de risque, et
 * l'email envoyé dix secondes plus tard aurait dit le contraire pour le
 * même dossier. On garde donc le NOMBRE du produit et on mappe les
 * variantes du brief sur le SENS : mauvaise nouvelle → aucune fête.
 */
export function gravity(score, t) {
  // `Number(null)` VAUT ZÉRO, ET ZÉRO EST FINI. Le garde-fou était donc
  // inopérant sur exactement le cas qu'il devait couvrir : une absence de
  // score. `gravity(null)` rendait la bande BASSE — verte, « SOLIDE »,
  // avec ses confettis — au lieu de `null`.
  //
  // Tant que la phase Verdict ne s'ouvrait que sur `dims && score`, rien
  // ne pouvait appeler cette fonction sans score et le piège dormait.
  // Depuis que les sept phases défilent au rythme (24/08/2026), la phase
  // passe à t+30 s : capture de recette à 0:32 sur un dossier non
  // analysé — « SCORE ARCHER 0 / 100 — GRAVITÉ FAIBLE », tampon
  // « SOLIDE », confettis, et « Tu as de bons arguments ». Le pire
  // mensonge que cet écran puisse produire.
  //
  // On refuse donc explicitement tout ce qui n'est pas un nombre, AVANT
  // la conversion — `null`, `undefined` et la chaîne vide y passent tous.
  if (score === null || score === undefined || score === '') return null;
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  if (n >= 61) {
    return {
      key: 'high', color: '#ea580c', soft: '#fff7ed', border: '#fed7aa',
      level: t('cine.risk_high'),
      // Aucun confetti, aucune posture de victoire — cf. le brief.
      celebrate: false,
      headline: t('cine.headline_high'),
      sub: t('cine.sub_high'),
      stamp: t('cine.stamp_high'),
    };
  }
  if (n >= 31) {
    return {
      key: 'mid', color: '#f59e0b', soft: '#fffbeb', border: '#fde68a',
      level: t('cine.risk_mid'),
      celebrate: false,
      headline: t('cine.headline_mid'),
      sub: t('cine.sub_mid'),
      stamp: t('cine.stamp_mid'),
    };
  }
  return {
    key: 'low', color: '#16a34a', soft: '#f0fdf4', border: '#bbf7d0',
    level: t('cine.risk_low'),
    celebrate: true,
    headline: t('cine.headline_low'),
    sub: t('cine.sub_low'),
    stamp: t('cine.stamp_low'),
  };
}

/**
 * Teinte d'une dimension.
 *
 * Ce barème était recopié ici — 70/40, rouge pur — et le commentaire disait
 * « même barème que la page dossier », ce qui décrivait une intention, pas
 * du code : rien n'était partagé, et les valeurs avaient divergé. Il y en
 * avait six dans le produit. `getDimensionTone` est désormais le seul, et
 * il porte le barème que le produit déclare unifié (61/31).
 */
export function dimTone(v) {
  const tone = getDimensionTone(v);
  return { c: tone.color, soft: tone.soft };
}

export const CINE_LOCALE = { fr: 'fr-BE', nl: 'nl-BE', en: 'en-GB' };
export const cineLocale = (lang) => CINE_LOCALE[lang] || CINE_LOCALE.fr;

export const eur = (n, locale) => Math.round(Number(n)).toLocaleString(locale || CINE_LOCALE.fr) + ' €';
export const nfr = (n, locale) => Number(n).toLocaleString(locale || CINE_LOCALE.fr);

/**
 * Libellé d'une passe d'analyse, traduit.
 *
 * `PASSES` (CinematiqueV5/useAnalysisFeed) porte des libellés français en
 * dur — c'est la structure du pipeline, partagée avec d'autres écrans, on
 * n'y touche pas. On mappe simplement sa `key` vers le dictionnaire.
 */
export const passLabel = (t, key) => t(`cine.pass_${key}`);
